/**
 * Remote FRA → LIVEFRA Google Sheet.
 * Browser raw POST loses body on Google redirect — we use hidden FORM POST
 * (e.parameter.data) which survives the redirect.
 */

const QUEUE_KEY = "fra-remote-log-queue";
const MAX_QUEUE = 80;

function getLogUrl() {
  if (typeof window === "undefined") return "";
  try {
    if (window.__FRA_LOG_URL__) return String(window.__FRA_LOG_URL__);
    const stored = localStorage.getItem("fra-log-url");
    if (stored) return stored;
  } catch (e) {
    /* ignore */
  }
  return "";
}

function isRemoteLogEnabled() {
  if (typeof window === "undefined") return false;
  try {
    if (window.__FRA_REMOTE_LOG__ === true) return true;
    if (window.location.search.includes("fra-log=1")) return true;
    if (localStorage.getItem("fra-remote-log") === "1") return true;
    return !!getLogUrl();
  } catch (e) {
    return false;
  }
}

function sessionId() {
  try {
    let id = sessionStorage.getItem("fra-session-id");
    if (!id) {
      id = `s_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      sessionStorage.setItem("fra-session-id", id);
    }
    return id;
  } catch (e) {
    return `s_${Date.now()}`;
  }
}

function readQueue() {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    return [];
  }
}

function writeQueue(items) {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(items.slice(-MAX_QUEUE)));
  } catch (e) {
    /* ignore */
  }
}

function enqueue(entry) {
  const q = readQueue();
  q.push(entry);
  writeQueue(q);
}

function compactEntry(entry) {
  return {
    time: entry.time,
    session: entry.session,
    event: entry.event,
    layerId: entry.layerId || "",
    lineId: entry.lineId || "",
    moveDist: entry.moveDist != null ? entry.moveDist : "",
    linesBefore: entry.linesBefore != null ? entry.linesBefore : "",
    linesAfter: entry.linesAfter != null ? entry.linesAfter : "",
    missing: entry.missing != null ? entry.missing : "",
    anglesLost: entry.anglesLost != null ? entry.anglesLost : "",
    buildingGone: entry.buildingGone != null ? entry.buildingGone : "",
    diagonalsBefore:
      entry.diagonalsBefore != null ? entry.diagonalsBefore : "",
    diagonalsAfter: entry.diagonalsAfter != null ? entry.diagonalsAfter : "",
  };
}

function ensureLogIframe() {
  const name = "fra_log_iframe";
  let iframe = document.getElementById(name);
  if (!iframe) {
    iframe = document.createElement("iframe");
    iframe.name = name;
    iframe.id = name;
    iframe.setAttribute("style", "display:none;width:0;height:0;border:0");
    document.body.appendChild(iframe);
  }
  return name;
}

/** Form POST — fields survive Apps Script 302 redirect (unlike raw fetch body). */
function sendViaForm(url, json) {
  if (typeof document === "undefined") return;
  const target = ensureLogIframe();
  const form = document.createElement("form");
  form.method = "POST";
  form.action = url;
  form.target = target;
  form.acceptCharset = "UTF-8";
  form.setAttribute("style", "display:none");

  const input = document.createElement("input");
  input.type = "hidden";
  input.name = "data";
  input.value = json;
  form.appendChild(input);

  document.body.appendChild(form);
  form.submit();
  setTimeout(() => {
    try {
      form.remove();
    } catch (e) {
      /* ignore */
    }
  }, 3000);
}

function sendViaGet(url, json) {
  try {
    if (json.length > 1600) return;
    const sep = url.indexOf("?") >= 0 ? "&" : "?";
    const ping = url + sep + "log=" + encodeURIComponent(json);
    const img = new Image();
    img.referrerPolicy = "no-referrer";
    img.src = ping;
  } catch (e) {
    /* ignore */
  }
}

function sendOne(url, entry) {
  const json = JSON.stringify(compactEntry(entry));
  try {
    sendViaForm(url, json);
  } catch (e) {
    /* ignore */
  }
  try {
    sendViaGet(url, json);
  } catch (e) {
    /* ignore */
  }
}

function flushQueue() {
  const url = getLogUrl();
  if (!url || !isRemoteLogEnabled()) return;

  const q = readQueue();
  if (!q.length) return;

  const batch = q.slice(0, 12);
  writeQueue(q.slice(batch.length));

  batch.forEach((entry, i) => {
    // Stagger so Apps Script isn't flooded
    setTimeout(() => {
      try {
        sendOne(url, entry);
      } catch (e) {
        enqueue(entry);
      }
    }, i * 350);
  });
}

export function fraRemoteLog(event, data = {}) {
  if (!isRemoteLogEnabled()) return;

  const entry = {
    time: new Date().toISOString(),
    session: sessionId(),
    event: String(event || "UNKNOWN"),
    ...data,
  };

  enqueue(entry);
  if (typeof window !== "undefined") {
    clearTimeout(window.__FRA_LOG_FLUSH_T__);
    window.__FRA_LOG_FLUSH_T__ = setTimeout(flushQueue, 300);
  }

  try {
    console.log("[FRA-REMOTE]", event, data);
  } catch (e) {
    /* ignore */
  }
}

export function fraRemoteLogError(where, err, extra = {}) {
  fraRemoteLog("ERROR", {
    where,
    message: err && err.message ? err.message : String(err),
    ...extra,
  });
}

if (typeof window !== "undefined") {
  window.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushQueue();
  });
  window.addEventListener("pagehide", flushQueue);
  window.fraRemoteLog = fraRemoteLog;
  window.fraFlushLogs = flushQueue;
}

export default fraRemoteLog;
